import { allPieces } from "@/utils/data"
import { host } from "@/utils/utils"

// The piece a player picked for themselves in settings. This is a *viewer's*
// preference, not a property of the seat: the server keeps handing out pieces by
// seat index as it always did, and each client swaps its own seat's piece for
// the chosen one before anything is drawn. So two people in the same game can
// both play "the car" — each of them sees their own token as the car, and sees
// the other player holding whichever piece got displaced.
//
// The account owns the choice (User.preferredPieceId), so it follows the player
// to any device. What's below is only a local cache of the last known value:
// the account data arrives a fetch after the app boots, and without it a game
// opened from a link would draw the dealt pieces first and visibly swap them a
// moment later. The cache is stamped with the email it belongs to so a shared
// browser never shows one account's piece to the next person to log in.
const CACHE_KEY = "game.preferredPieceId"

interface ICachedPreference {
  email: string
  pieceId: string
}

const isKnownPiece = (pieceId: unknown): pieceId is string =>
  allPieces.some((piece) => piece.id === pieceId)

export const getCachedPreferredPieceId = (
  email: string | undefined
): string | undefined => {
  if (!email) return undefined
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return undefined
    const cached = JSON.parse(raw) as ICachedPreference
    // A stale id from an older build, or another account's cache, counts as no
    // preference rather than as something to draw.
    if (cached?.email !== email || !isKnownPiece(cached?.pieceId)) {
      return undefined
    }
    return cached.pieceId
  } catch {
    return undefined
  }
}

export const setCachedPreferredPieceId = (
  email: string | undefined,
  pieceId: string | undefined
): void => {
  if (!email || !pieceId) localStorage.removeItem(CACHE_KEY)
  else localStorage.setItem(CACHE_KEY, JSON.stringify({ email, pieceId }))
}

/**
 * Save the choice on the account. Resolves false when it could not be stored
 * (offline, or a guest with no account behind them) so the caller can say so —
 * the piece still applies for this session either way.
 */
export const savePreferredPieceIdToAccount = async (
  email: string | undefined,
  pieceId: string | undefined
): Promise<boolean> => {
  if (!email) return false
  try {
    const res = await fetch(`${host}/api/user/preferred-piece`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, pieceId: pieceId ?? null }),
    })
    return res.ok
  } catch {
    return false
  }
}

/**
 * Swap the viewer's seat onto their preferred piece, and hand whoever was
 * holding that piece the seat's original one. A straight transposition, so the
 * six pieces stay unique — nobody ends up looking at two identical tokens on the
 * board.
 *
 * The original assignment is kept on `basePieceId`, and every run recomputes
 * from it rather than from the last result. That makes the transform idempotent:
 * re-applying it on each situation update is a no-op, and changing the
 * preference mid-game moves the table straight to the new arrangement instead of
 * shuffling one swap further every time.
 */
export const applyPiecePreference = (
  players: IPlayer[] | undefined,
  preferredPieceId: string | undefined,
  myEmail: string
): IPlayer[] | undefined => {
  if (!players?.length) return players
  const me = players.find((player) => player.id === myEmail)
  const myBasePieceId = me ? me.basePieceId ?? me.pieceId : undefined
  // Nothing to do for a spectator, for a seat that already holds the wanted
  // piece, or when no preference is set at all.
  const swapping =
    !!me && !!preferredPieceId && myBasePieceId !== preferredPieceId
  if (
    !swapping &&
    !players.some((player) => player.basePieceId !== undefined)
  ) {
    return players
  }
  return players.map((player) => {
    const basePieceId = player.basePieceId ?? player.pieceId
    const pieceId = !swapping
      ? basePieceId
      : player.id === me!.id
      ? (preferredPieceId as string)
      : basePieceId === preferredPieceId
      ? (myBasePieceId as string)
      : basePieceId
    // Keep the object identity when nothing moved, so memoised player cards
    // don't re-render on every situation update.
    if (player.pieceId === pieceId && player.basePieceId === basePieceId) {
      return player
    }
    return { ...player, basePieceId, pieceId }
  })
}

/**
 * Same swap, applied to a whole game object. Used by the reducers that take game
 * data in from the socket, so every screen reading `myGame` from the store gets
 * the remapped pieces without knowing this feature exists.
 */
export const applyPiecePreferenceToGame = (
  game: IGameDetails | undefined,
  preferredPieceId: string | undefined,
  myEmail: string
): IGameDetails | undefined => {
  const situation = game?.situation
  const players = situation?.players
  if (!game || !situation || !players?.length) return game
  // Cheap exit for the overwhelmingly common case (no preference, untouched
  // server data) so the per-update cost stays at one array check.
  if (
    !preferredPieceId &&
    !players.some((player) => player.basePieceId !== undefined)
  ) {
    return game
  }
  const nextPlayers = applyPiecePreference(players, preferredPieceId, myEmail)
  if (nextPlayers === players) return game
  return {
    ...game,
    situation: { ...situation, players: nextPlayers as IPlayer[] },
  }
}
